
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminUsers } from "@/hooks/useAdminUsers";
import Header from "@/components/Header";
import AdminHeader from "@/components/admin/AdminHeader";
import UserStatusCard from "@/components/admin/UserStatusCard";
import UsersTable from "@/components/admin/UsersTable";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Admin = () => {
  const { 
    users, 
    loading, 
    updating, 
    isAuthorized,
    togglePremium, 
    formatDate, 
    getCurrentUserIsPremium 
  } = useAdminUsers();
  
  const [filter, setFilter] = useState<"all" | "premium" | "free">("all");
  const navigate = useNavigate();
  
  // Only redirect if authorization check has completed and user is not authorized
  useEffect(() => {
    if (isAuthorized === false) {
      console.log("User is not authorized, redirecting to home page");
      navigate('/');
    } else if (isAuthorized === true) {
      console.log("User is authorized to view admin page");
    }
  }, [isAuthorized, navigate]);
  
  // Show loading state while checking authorization
  if (isAuthorized === null) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Don't render anything if not authorized
  if (isAuthorized === false) {
    return null;
  }
  
  const filteredUsers = users.filter(user => {
    if (filter === "all") return true;
    if (filter === "premium") return user.is_premium;
    if (filter === "free") return !user.is_premium;
    return true;
  });

  return (
    <main className="min-h-screen pt-24 pb-16">
      <Header />
      <div className="container max-w-6xl mx-auto px-4">
        <AdminHeader />
        <UserStatusCard 
          togglePremium={togglePremium}
          getCurrentUserIsPremium={getCurrentUserIsPremium}
          updating={updating}
        />
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle>All Users</CardTitle>
              <CardDescription>
                Manage premium status for all users in the system
              </CardDescription>
            </div>
            <Select value={filter} onValueChange={(value) => setFilter(value as "all" | "premium" | "free")}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="premium">Premium Users</SelectItem>
                <SelectItem value="free">Free Users</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <UsersTable 
              users={filteredUsers}
              loading={loading}
              updating={updating}
              togglePremium={togglePremium}
              formatDate={formatDate}
            />
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default Admin;
